/**
 * Item Library Integration for Geowall & Tobermore Walls
 * Maps field names to rates from Item Library API
 */

import { ItemLibraryResponse, ItemLibraryItem } from '../api/itemLibrary';
import { GEO_TOBER_WALL_REFERENCES } from './geoToberWallReferenceMapping';
import { GEO_TOBER_WALL_FIELD_LABELS, GEO_TOBER_WALL_FIELD_UNITS } from './geoToberWallFieldMapping';

export interface RateInfo {
  rate: number;
  description: string;
  unit: string;
}

/**
 * Get rate from item library by resource code
 */
function getRateByCode(
  resourceCode: string,
  resourceCodeMap: Map<string, ItemLibraryItem>,
  fieldName: string,
  fallbackDescription?: string,
): RateInfo {
  const normalizedCode = resourceCode.trim().toUpperCase();
  const item = resourceCodeMap.get(normalizedCode);

  if (item) {
    return {
      rate: item.nettRate || item.baseRate || 0,
      description: item.resourceDescription || fallbackDescription || fieldName,
      unit: item.unit || 'm³',
    };
  }

  // Item not found - use fallback description if provided
  return {
    rate: 0,
    description: fallbackDescription || fieldName,
    unit: 'm³',
  };
}

/**
 * Create a rate lookup map from item library for Geowall & Tobermore Walls
 * @param itemLibraryData - Item library response from API
 */
export function createGeoToberWallRateLookupMap(
  itemLibraryData: ItemLibraryResponse | undefined,
): Map<string, RateInfo> {
  const rateMap = new Map<string, RateInfo>();

  if (!itemLibraryData) {
    return rateMap;
  }

  // Create a map of resourceCode to item for quick lookup
  const resourceCodeMap = new Map<string, ItemLibraryItem>();
  itemLibraryData.forEach((item: ItemLibraryItem) => {
    const code = item.resourceCode?.trim().toUpperCase();
    if (code) {
      resourceCodeMap.set(code, item);
    }
  });

  // Map each field name to its rate info (rate, description, unit)
  Object.entries(GEO_TOBER_WALL_REFERENCES).forEach(([fieldName, reference]) => {
    // Get proper label from field mapping as fallback
    const fallbackDescription = GEO_TOBER_WALL_FIELD_LABELS[fieldName] || fieldName;

    if (!reference.code) {
      // Fields without codes (Tobermore items with no build up in Evaluate)
      rateMap.set(fieldName, {
        rate: 0,
        description: fallbackDescription,
        unit: GEO_TOBER_WALL_FIELD_UNITS[fieldName] ?? '',
      });
      return;
    }

    // Static code from reference mapping
    const rateInfo = getRateByCode(reference.code, resourceCodeMap, fieldName, fallbackDescription);
    rateMap.set(fieldName, { ...rateInfo, unit: fieldName in GEO_TOBER_WALL_FIELD_UNITS ? GEO_TOBER_WALL_FIELD_UNITS[fieldName] : rateInfo.unit });
  });

  return rateMap;
}

/**
 * Get rate info for a specific field name
 */
export function getGeoToberWallRateInfo(
  fieldName: string,
  rateLookupMap: Map<string, RateInfo>,
): RateInfo {
  return rateLookupMap.get(fieldName) || { rate: 0, description: fieldName, unit: GEO_TOBER_WALL_FIELD_UNITS[fieldName] ?? '' };
}

/**
 * Apply rates and descriptions to Geowall & Tobermore Walls summary items
 */
export function applyGeoToberWallRatesToSummary(
  summaryItems: any[],
  rateLookupMap: Map<string, RateInfo>,
): any[] {
  return summaryItems.map((item) => {
    const rateInfo = getGeoToberWallRateInfo(item.fieldName, rateLookupMap);
    const amount = Math.abs(item.quantity * rateInfo.rate);

    return {
      ...item,
      rate: rateInfo.rate,
      description: item.description,
      unit: rateInfo.unit,
      amount,
    };
  });
}
